import { TIMEZONE_OPTIONS } from '@act/shared/constants';
import {
  CreateInternalPhoneNumberRequest,
  CreateInternalPhoneNumberSchema,
  FilterOptionDto,
  InternalPhoneNumberAdminViewDto,
  UpdateInternalPhoneNumberRequest,
  UpdateInternalPhoneNumberSchema,
  UserAdministrationDto,
} from '@act/shared/data-transfer-objects';
import { FormBuilder } from '@act/storybook';
import { zodResolver } from '@hookform/resolvers/zod';
import { Box } from '@mui/material';
import { useEffect, useMemo } from 'react';
import { useToast } from 'react-components/app/ReusableToast';
import useUnsavedChangesDialog from 'react-components/dialog-forms/hooks/use-unsaved-changes-dialog';
import { useForm } from 'react-hook-form';
import {
  createPhoneNumberSuccess,
  updatePhoneNumberSuccess,
} from 'redux/slices/internalPhoneNumberSlice';
import { InternalPhoneNumberRequests } from 'redux/requests/internal-phone-numbers.requests';
import { useAppDispatch } from 'redux/store-hooks';
import { useApiCall } from 'redux/utils/api-hook';
import { FormConfig } from 'stories/forms/FormBuilder/FormBuilder.types';
import { AdminDrawerForm } from '../AdminDrawer';

interface Props {
  users: UserAdministrationDto[];
  setOpen: (value: boolean) => void;
  open: boolean;
  isEdit: boolean;
  selectedPhone?: InternalPhoneNumberAdminViewDto;
}

export const PhoneNumberCreateEdit = ({
  setOpen,
  open,
  users,
  isEdit,
  selectedPhone,
}: Props) => {
  const dispatch = useAppDispatch();
  const toast = useToast();
  const handleUnsavedChanges = useUnsavedChangesDialog();
  const [internalPhoneNumberResult, isCreating, createInternalPhoneNumber] =
    useApiCall(InternalPhoneNumberRequests.createInternalPhoneNumber);
  const [
    updateInternalPhoneNumberResult,
    isUpdating,
    updateInternalPhoneNumber,
  ] = useApiCall(InternalPhoneNumberRequests.updateInternalPhoneNumber);

  const schema = isEdit
    ? UpdateInternalPhoneNumberSchema
    : CreateInternalPhoneNumberSchema;

  const { control, handleSubmit, formState } = useForm<
    CreateInternalPhoneNumberRequest | UpdateInternalPhoneNumberRequest
  >({
    resolver: zodResolver(schema as any),
    defaultValues: {
      displayName: selectedPhone?.displayName ?? '',
      description: selectedPhone?.description ?? '',
      number: selectedPhone?.number ?? undefined,
      timezone: selectedPhone?.timezone ?? undefined,
      isDefault: selectedPhone?.isDefault ?? false,
      isFeedbackNumber: selectedPhone?.isFeedbackNumber ?? false,
      sid: selectedPhone?.sid ?? '',
      supportUserIds: selectedPhone?.supportUserIds ?? undefined,
      friendlyName: selectedPhone?.friendlyName ?? '',
    },
  });

  const userOptions: FilterOptionDto[] = users.map(user => ({
    label: `${user.firstName} ${user.lastName}`,
    value: user.id,
  }));

  const formConfig: FormConfig<
    CreateInternalPhoneNumberRequest | UpdateInternalPhoneNumberRequest
  > = [
    {
      type: 'group',
      items: [
        {
          type: 'input',
          name: 'isDefault',
          label: 'Default Number',
          input: 'checkbox',
          props: {
            defaultValue: false,
          },
        },
        {
          type: 'input',
          name: 'isFeedbackNumber',
          label: 'Feedback Number',
          input: 'checkbox',
          props: {
            defaultValue: false,
          },
        },
      ],
    },
    {
      type: 'input',
      name: 'displayName',
      label: 'Display Name',
      input: 'text',
    },
    {
      type: 'input',
      name: 'description',
      label: 'Description',
      input: 'text',
    },
    {
      type: 'input',
      name: 'number',
      label: 'Phone number',
      input: 'phone',
    },
    {
      type: 'input',
      name: 'timezone',
      label: 'Timezone',
      input: 'select',
      props: {
        options: TIMEZONE_OPTIONS.map(option => ({
          label: option.replace('_', ' '),
          value: option,
        })) as Array<{ label: string; value: string }>,
        fullWidth: true,
      },
    },
    {
      type: 'input',
      name: 'sid',
      label: 'SID',
      input: 'text',
    },
    {
      type: 'input',
      name: 'friendlyName',
      label: 'Friendly Name',
      input: 'text',
    },
    {
      type: 'input',
      name: 'supportUserIds',
      label: 'Select Support Users',
      input: 'autocomplete',
      props: {
        options: userOptions,
        multiple: true,
        limitTags: 2,
        getOptionLabel: (option: FilterOptionDto) => option.label,
      },
    },
  ];

  const onSubmit = async (data: CreateInternalPhoneNumberRequest) => {
    if (isEdit) {
      updateInternalPhoneNumber({
        params: { id: selectedPhone?.id },
        body: data,
      });
    } else {
      createInternalPhoneNumber({
        body: data,
        params: null,
      });
    }
  };

  // Handle successful creation
  useEffect(() => {
    if (internalPhoneNumberResult && !isCreating) {
      dispatch(createPhoneNumberSuccess(internalPhoneNumberResult));
      toast('Phone number created successfully', 'success');
      setOpen(false);
    }
  }, [internalPhoneNumberResult, isCreating, dispatch, setOpen]);

  // Handle successful update
  useEffect(() => {
    if (updateInternalPhoneNumberResult && !isUpdating) {
      dispatch(updatePhoneNumberSuccess(updateInternalPhoneNumberResult));
      toast('Phone number updated successfully', 'success');
      setOpen(false);
    }
  }, [updateInternalPhoneNumberResult, isUpdating, dispatch, setOpen]);

  const { isDirty } = formState;

  const handleClose = () => {
    if (isDirty) {
      void handleUnsavedChanges(() => {
        setOpen(false);
      });
    } else {
      setOpen(false);
    }
  };

  const DrawerContent = useMemo(() => {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <FormBuilder
          config={formConfig}
          control={control}
          formState={formState}
        />
      </Box>
    );
  }, [control, formConfig, formState.errors]);

  return (
    <AdminDrawerForm
      title={isEdit ? `Edit Phone Number` : `Add New Phone Number`}
      children={DrawerContent}
      onSubmit={handleSubmit(onSubmit)}
      open={open}
      setOpen={handleClose}
    />
  );
};
